/**
 * @fileoverview Offering to put MapShare on the phone's home screen
 * (F-J-02, pulled into P1 on 2026-09-28, OQ-58).
 *
 * Browsers differ in what a page may do about it:
 *
 * - **Chromium (Android's Chrome, Edge, Samsung Internet)** fires
 *   `beforeinstallprompt` once the page is installable (`public/manifest.webmanifest`).
 *   Keeping that event lets a button of ours open the browser's own install
 *   dialog. It is fired early — often before the organization's chunk has
 *   loaded — so `captureInstallPrompt()` runs from `main.tsx`, and this module
 *   holds the event until a screen asks.
 * - **Safari on iPhone and iPad** has no such event, and no way for a page to
 *   install itself. All a page can do is say where the browser's own
 *   「ホーム画面に追加」 is.
 * - **Anything else** (Firefox on Android, a desktop browser that never fired
 *   the event) is offered nothing: there would be nothing true to say.
 *
 * Nothing is offered once MapShare is open from the home screen (standalone).
 * Chromium also stops firing the event once the app is installed; Safari cannot
 * tell, so there the offer can be put away (`dismissInstallCard`).
 */

/** Chromium's `beforeinstallprompt` event: not in the DOM typings. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * - `prompt`: the browser will show its own install dialog (Chromium)
 * - `ios`: say where Safari's 「ホーム画面に追加」 is
 */
export type InstallOffer = 'prompt' | 'ios';

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Keeps `beforeinstallprompt` for later, from the moment the page starts.
 * `preventDefault()` keeps Chrome's own mini-infobar away: the offer is ours
 * to make, on the organization's map, and not on the landing page, where an
 * installed app would open the landing page.
 */
export function captureInstallPrompt(target: Window = window): void {
  target.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });
  target.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

/** Called whenever the held event arrives or goes away. */
export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function hasInstallPrompt(): boolean {
  return deferred !== null;
}

/**
 * Opens the browser's install dialog. The event can be used only once, so it
 * is let go whatever the answer; if the person declined, Chromium fires a new
 * one later.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (event === null) return 'unavailable';
  deferred = null;
  notify();
  await event.prompt();
  return (await event.userChoice).outcome;
}

/** Open from the home screen already: the manifest's `display`, or iOS's own flag. */
export function isStandalone(win: Window = window): boolean {
  const standaloneQuery =
    typeof win.matchMedia === 'function' && win.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = (win.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standaloneQuery || iosStandalone;
}

/**
 * iPhone, iPod, or iPad — which since iPadOS 13 calls itself a Mac, and is
 * told from one by its touch screen.
 */
export function isIos(userAgent: string, maxTouchPoints: number): boolean {
  if (/iPhone|iPad|iPod/.test(userAgent)) return true;
  return /Macintosh/.test(userAgent) && maxTouchPoints > 1;
}

export function installOffer(input: {
  standalone: boolean;
  hasPrompt: boolean;
  userAgent: string;
  maxTouchPoints: number;
}): InstallOffer | null {
  if (input.standalone) return null;
  if (input.hasPrompt) return 'prompt';
  if (isIos(input.userAgent, input.maxTouchPoints)) return 'ios';
  return null;
}

/** What this browser can be offered right now. */
export function currentInstallOffer(win: Window = window): InstallOffer | null {
  return installOffer({
    standalone: isStandalone(win),
    hasPrompt: hasInstallPrompt(),
    userAgent: win.navigator.userAgent,
    maxTouchPoints: win.navigator.maxTouchPoints ?? 0,
  });
}

const DISMISSED_KEY = 'mapshare:install-card:dismissed-at';

/** How long 「今はしない」 puts the card on the map away. The menu still offers it. */
export const DISMISS_DAYS = 30;

export function isInstallCardDismissed(storage: Storage | undefined, now: number): boolean {
  try {
    const raw = storage?.getItem(DISMISSED_KEY);
    if (raw === null || raw === undefined) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return now - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function dismissInstallCard(storage: Storage | undefined, now: number): void {
  try {
    storage?.setItem(DISMISSED_KEY, String(now));
  } catch {
    // Private mode and the like: the card comes back next time, which is harmless.
  }
}

/** For tests: forget a held event. */
export function resetInstallPromptForTest(): void {
  deferred = null;
  notify();
}
